import { Inject, Injectable, OnDestroy } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';

import { combineLatest, filter, map, Subscription } from 'rxjs';

import { AbstractChartOptionsModel } from '../../../../Common/Charts/models/abstract-chart-options.model';
import { ChartStylesModel } from '../../../../Common/Charts/models/chart-styles.model';
import { DateAndValueXYChartOptionsModel } from '../../../../Common/Charts/models/date-and-value-x-y-chart-options.model';
import { MultiSeriesPieChartOptionsModel } from '../../../../Common/Charts/models/pieChart/multiSeriesPieChart/multi-series-pie-chart-options.model';
import { StackedBarChartOptionsModel } from '../../../../Common/Charts/models/stackedBarChart/stacked-bar-chart-options.model';
import { SocialChartTooltipService } from '../../../Shared/services/social-chart-tooltip.service';
import { ThemeAnalyticsChartTypeEnum } from '../../enums/theme-analytics-chart-type.enum';
import { DateAndValueXYChartUnitEnum } from '../../../../Common/Charts/enums/date-and-value-x-y-chart-unit.enum';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { SocialRoutingService } from '../../../Core/services/social-routing.service';
import { userAppPreferencesDefault } from '../../../../Core/Users/data/user-app-preferences-default';
import { socialNetworkTypeColorMap } from '../../../../Core/Users/data/social-network-type-color-map';
import { defaultSocialColorMap } from '../../../../Core/Users/data/default-social-color-map';
import { chartColors } from '../../../../Common/Charts/enums/chart-colors.enum';
import { EngagementTypeEnum } from '../../../Core/enums/engagement-type.enum';
import { NetworkTypeEnum } from '@Social/Core/enums/network-type.enum';
import { MonitorConfigService } from '@Common/MonitorShared/services/monitor-config.service';
import { mediaTypeToNetworkTypeMap } from '@Social/Intelligence/models/network-type-to-media-type-model.map';

@Injectable()
export class ThemeAnalyticsChartOptionsService implements OnDestroy {
    readonly defaultChartColors = userAppPreferencesDefault.reportsPrefs.defaultChartColors;

    sentimentSummaryChartOptions: MultiSeriesPieChartOptionsModel;
    shareOfVoiceBySocialNetworkChartOptions: AbstractChartOptionsModel;
    shareOfVoiceBySocialNetworkSubChartOptions: MultiSeriesPieChartOptionsModel;
    shareOfVoiceBySocialTopicSubChartOptions: MultiSeriesPieChartOptionsModel;
    sentimentBySocialMediaComparisionChartOptions: StackedBarChartOptionsModel;
    sentimentBySocialTopicComparisionChartOptions: StackedBarChartOptionsModel;
    socialMediaSentimentTrendChartOptions: DateAndValueXYChartOptionsModel;
    socialTopicSentimentTrendChartOptions: DateAndValueXYChartOptionsModel;
    summaryBySocialTopicChartOptions: DateAndValueXYChartOptionsModel;
    summaryBySocialNetworkChartOptions: DateAndValueXYChartOptionsModel;
    engagementChartOptions: MultiSeriesPieChartOptionsModel;
    chartTypeToOptionsMap: Map<ThemeAnalyticsChartTypeEnum, AbstractChartOptionsModel>;

    isArchiveSearch: boolean = false;
    private subscriptions: Subscription[] = [];
    availableNetworks: NetworkTypeEnum[];

    constructor(
        public socialChartTooltipService: SocialChartTooltipService,
        public localizationService: LocalizationService,
        private socialRoutingService: SocialRoutingService,
        private monitorConfigService: MonitorConfigService,
        @Inject(APP_BASE_HREF) public appBaseHref: string
    ) {
        this.subscriptions.push(
            combineLatest([
                this.socialRoutingService.isArchiveSearch$,
                this.monitorConfigService.getAvailableSocialMediaTypes().pipe(filter(x => !!x))
            ])
            .subscribe(([isArchiveSearch, mediaTypes]) => {
                this.isArchiveSearch = isArchiveSearch;
                this.availableNetworks = mediaTypes.map(mediaType => mediaTypeToNetworkTypeMap.get(mediaType)).filter(x => !!x);

                this.setSentimentSummaryChartOptions();
                this.setShareOfVoiceBySocialChartOptions();
                this.setShareOfVoiceBySocialSub2ChartOptions();
                this.setShareOfVoiceBySocialSubChartOptions();
                this.setSocialMediaSentimentTrendChartOptions();
                this.setSocialTopicSentimentTrendChartOptions();
                this.setSentimentBySocialMediaComparisonChartOptions();
                this.setSentimentBySocialTopicComparisonChartOptions();
                this.setSummaryBySocialTopicChartOptions();
                this.setSummaryBySocialNetworkChartOptions();
                this.setEngagementChartOptions();

                this.chartTypeToOptionsMap = new Map<ThemeAnalyticsChartTypeEnum, AbstractChartOptionsModel>(
                    [
                        [ThemeAnalyticsChartTypeEnum.SENTIMENT_SUMMARY, this.sentimentSummaryChartOptions],
                        [ThemeAnalyticsChartTypeEnum.SHARE_OF_VOICE_BY_SOCIAL_NETWORK, this.shareOfVoiceBySocialNetworkChartOptions],
                        [ThemeAnalyticsChartTypeEnum.SHARE_OF_VOICE_BY_SOCIAL_NETWORK_SUB, this.shareOfVoiceBySocialNetworkSubChartOptions],
                        [ThemeAnalyticsChartTypeEnum.SHARE_OF_VOICE_BY_SOCIAL_TOPIC_SUB, this.shareOfVoiceBySocialTopicSubChartOptions],
                        [ThemeAnalyticsChartTypeEnum.SENTIMENT_BY_SOCIAL_MEDIA_COMPARISON, this.sentimentBySocialMediaComparisionChartOptions],
                        [ThemeAnalyticsChartTypeEnum.SENTIMENT_BY_SOCIAL_TOPIC_COMPARISON, this.sentimentBySocialTopicComparisionChartOptions],
                        [ThemeAnalyticsChartTypeEnum.SOCIAL_MEDIA_SENTIMENT_TREND, this.socialMediaSentimentTrendChartOptions],
                        [ThemeAnalyticsChartTypeEnum.SOCIAL_TOPIC_SENTIMENT_TREND, this.socialTopicSentimentTrendChartOptions],
                        [ThemeAnalyticsChartTypeEnum.SUMMARY_BY_SOCIAL_TOPIC, this.summaryBySocialTopicChartOptions],
                        [ThemeAnalyticsChartTypeEnum.SUMMARY_BY_SOCIAL_NETWORK, this.summaryBySocialNetworkChartOptions],
                        [ThemeAnalyticsChartTypeEnum.SOCIAL_MEDIA_ENGAGEMENT, this.engagementChartOptions]
                    ]
                );
            })
        );
    }

    private setSentimentSummaryChartOptions(): void {
        const styles = new ChartStylesModel();
        styles.chartHeight = 290;
        styles.titleFontSize = 18;
        styles.fontWeight = '700';
        styles.innerRadiusPercent = 15;
        styles.seriesRadiusPercent = this.isArchiveSearch ? [60] : [35, 60];
        styles.titleLabelColor = this.defaultChartColors.otherColors.textColor;
        styles.colorMap = {
            'POSITIVE': this.defaultChartColors.sentimentColors.positive,
            'POSITIVE_LABEL': chartColors.agprWhite,
            'NEUTRAL': this.defaultChartColors.sentimentColors.neutral,
            'NEUTRAL_LABEL': this.defaultChartColors.otherColors.textColor,
            'NEGATIVE': this.defaultChartColors.sentimentColors.negative,
            'NEGATIVE_LABEL': chartColors.agprWhite,
            'NOT_AVAILABLE': this.defaultChartColors.sentimentColors.notAvailable,
            'NOT_AVAILABLE_LABEL': this.defaultChartColors.otherColors.textColor,
        };
        this.sentimentSummaryChartOptions = {
            mentionsLabel: 'Label',
            styles: styles,
            customLegendItems: [
                {
                    label: this.localizationService.transform('Reports.sentiment.positive'),
                    color: this.defaultChartColors.sentimentColors.positive
                },
                {
                    label: this.localizationService.transform('Reports.sentiment.neutral'),
                    color: this.defaultChartColors.sentimentColors.neutral
                },
                {
                    label: this.localizationService.transform('Reports.sentiment.negative'),
                    color: this.defaultChartColors.sentimentColors.negative
                },
                {
                    label: this.localizationService.transform('Reports.sentiment.notAvailable'),
                    color: this.defaultChartColors.sentimentColors.notAvailable
                },
            ],
            tooltipHTML: this.socialChartTooltipService.getTooltipHTML(
                'sentimentSummaryChart',
                '{category}{labelInTooltip}',
                '{value.value} ({value.percent.formatNumber(\'#.0\')}%)',
                null,
                'fa-solid fa-square'
            ),
            isSocialStyles: true
        };
    }

    private setShareOfVoiceBySocialChartOptions(): void {
        const chartGroupStyles = new ChartStylesModel();
        chartGroupStyles.chartHeight = 360;
        chartGroupStyles.legendItemHorizontalPadding = 15;
        chartGroupStyles.titleLabelColor = this.defaultChartColors.otherColors.textColor;

        const legendItems = this.availableNetworks.map(network => ({
            name: this.localizationService.transform(`MediaType.${network}`),
            fill: this.defaultChartColors.socialNetworkColors[network.toLowerCase()]
        }));

        this.shareOfVoiceBySocialNetworkChartOptions = {
            styles: chartGroupStyles,
            noPadding: true,
            customLegendItems: legendItems,
            isSocialStyles: true
        };
    }

    private setShareOfVoiceBySocialSub2ChartOptions(): void {
        const socialTopicSubChartOptionsStyles = new ChartStylesModel();
        socialTopicSubChartOptionsStyles.titleFontSize = 18;
        socialTopicSubChartOptionsStyles.titleAlignment = 'middle';
        socialTopicSubChartOptionsStyles.innerRadiusPercent = this.isArchiveSearch ? 35 : 15;
        socialTopicSubChartOptionsStyles.seriesRadiusPercent = [35, 60];
        socialTopicSubChartOptionsStyles.titleLabelColor = this.defaultChartColors.otherColors.textColor;
        socialTopicSubChartOptionsStyles.colorMap = socialNetworkTypeColorMap;

        this.shareOfVoiceBySocialNetworkSubChartOptions = {
            styles: socialTopicSubChartOptionsStyles,
            tooltipHTML: this.socialChartTooltipService.getTooltipHTML(
                'shareOfVoiceBySocialNetwork',
                '{category}{labelInTooltip}',
                '{value.value} ({value.percent.formatNumber(\'#.0\')}%)',
                null,
                'fa-solid fa-square'
            ),
            isSocialStyles: true
        };
    }

    private setShareOfVoiceBySocialSubChartOptions(): void {
        const socialTopicSubChartOptionsStyles = new ChartStylesModel();
        socialTopicSubChartOptionsStyles.titleFontSize = 18;
        socialTopicSubChartOptionsStyles.titleAlignment = 'middle';
        socialTopicSubChartOptionsStyles.innerRadiusPercent = 15;
        socialTopicSubChartOptionsStyles.seriesRadiusPercent = [35, 60];
        socialTopicSubChartOptionsStyles.titleLabelColor = this.defaultChartColors.otherColors.textColor;
        socialTopicSubChartOptionsStyles.colorMap = defaultSocialColorMap;

        this.shareOfVoiceBySocialTopicSubChartOptions = {
            styles: socialTopicSubChartOptionsStyles,
            tooltipHTML: this.socialChartTooltipService.getTooltipHTML(
                'shareOfVoiceBySocialTopic',
                '{category}{labelInTooltip}',
                '{value.value} ({value.percent.formatNumber(\'#.0\')}%)',
                null,
                'fa-solid fa-square'
            ),
            isSocialStyles: true
        };
    }

    private setSentimentBySocialMediaComparisonChartOptions(): void {
        const chartStyles = new ChartStylesModel();
        chartStyles.colorMap = {
            'POSITIVE': this.defaultChartColors.sentimentColors.positive,
            'POSITIVE_LABEL': chartColors.agprWhite,
            'NEUTRAL': this.defaultChartColors.sentimentColors.neutral,
            'NEUTRAL_LABEL': this.defaultChartColors.otherColors.textColor,
            'NEGATIVE': this.defaultChartColors.sentimentColors.negative,
            'NEGATIVE_LABEL': chartColors.agprWhite,
            'NOT_AVAILABLE': this.defaultChartColors.sentimentColors.notAvailable,
            'NOT_AVAILABLE_LABEL': this.defaultChartColors.otherColors.textColor
        };
        chartStyles.customPadding = {
            top: 20,
            right: 20,
            bottom: 10,
            left: 20
        };
        chartStyles.chartHeight = 400;
        chartStyles.titleFontSize = 18;
        chartStyles.titleLabelColorc = this.defaultChartColors.otherColors.textColor;
        chartStyles.subtitleFontSize = 14;
        chartStyles.subtitleLabelColor = this.defaultChartColors.otherColors.textColor;

        this.sentimentBySocialMediaComparisionChartOptions = {
            styles: chartStyles,
            label: 'Label',
            seriesIds: [
                'POSITIVE',
                'NEUTRAL',
                'NEGATIVE',
                'NOT_AVAILABLE'

            ],
            seriesLabels: [
                this.localizationService.transform('Reports.sentiment.positive'),
                this.localizationService.transform('Reports.sentiment.neutral'),
                this.localizationService.transform('Reports.sentiment.negative'),
                this.localizationService.transform('Reports.sentiment.notAvailable')

            ],
            hundredPercentStacked: true,
            showValueAxis: true,
            imageAsCategory: true,
            appBaseHref: this.appBaseHref,
            hoverEffect: true,
            sortValues: false,
            adjustedBarHeightPercent: 75,
            bulletLabelFontWeight: 'normal',
            tooltipText:
                '[bold font-size:0.75rem]{categoryY}: [font-size: 0.75rem]{name}\n[font-size:0.75rem]{valueX} ({valueX.totalPercent.formatNumber(\'#.0\')}%)',
            titleTooltipText: '',
            subtitleTooltipText: '',
            tooltipHTML: this.socialChartTooltipService.getTooltipHTML(
                'sentimentBySocialMediaComparisonChart',
                '{categoryY}',
                '{valueX} ({valueX.totalPercent.formatNumber(\'#.0\')}%)',
                '{name}',
                'fa-solid fa-square'
            ),
            isSocialStyles: true,
            'POSITIVE': {
                countLabel: this.localizationService.transform('Reports.sentiment.positive.count'),
                percentLabel: this.localizationService.transform('Reports.sentiment.positive.percent')
            },
            'NEGATIVE': {
                countLabel: this.localizationService.transform('Reports.sentiment.negative.count'),
                percentLabel: this.localizationService.transform('Reports.sentiment.negative.percent')
            },
            'NEUTRAL': {
                countLabel: this.localizationService.transform('Reports.sentiment.neutral.count'),
                percentLabel: this.localizationService.transform('Reports.sentiment.neutral.percent')
            },
            'NOT_AVAILABLE': {
                countLabel: this.localizationService.transform('Reports.sentiment.notavailable.count'),
                percentLabel: this.localizationService.transform('Reports.sentiment.notavailable.percent')
            }
        };
    }

    private setSentimentBySocialTopicComparisonChartOptions(): void {
        const chartStyles = new ChartStylesModel();
        chartStyles.colorMap = {
            'POSITIVE': this.defaultChartColors.sentimentColors.positive,
            'POSITIVE_LABEL': chartColors.agprWhite,
            'NEUTRAL': this.defaultChartColors.sentimentColors.neutral,
            'NEUTRAL_LABEL': this.defaultChartColors.otherColors.textColor,
            'NEGATIVE': this.defaultChartColors.sentimentColors.negative,
            'NEGATIVE_LABEL': chartColors.agprWhite,
            'NOT_AVAILABLE': chartColors.agprNotAvailable,
            'NOT_AVAILABLE_LABEL': this.defaultChartColors.otherColors.textColor
        };
        chartStyles.customPadding = {
            top: 20,
            right: 20,
            bottom: 10,
            left: 20
        };
        chartStyles.chartHeight = 400;
        chartStyles.titleFontSize = 18;
        chartStyles.titleLabelColorc = this.defaultChartColors.otherColors.textColor;
        chartStyles.subtitleFontSize = 14;
        chartStyles.subtitleLabelColor = this.defaultChartColors.otherColors.textColor;

        this.sentimentBySocialTopicComparisionChartOptions = {
            styles: chartStyles,
            label: 'Label',
            seriesIds: [
                'POSITIVE',
                'NEUTRAL',
                'NEGATIVE',
                'NOT_AVAILABLE'
            ],
            seriesLabels: [
                this.localizationService.transform('Reports.sentiment.positive'),
                this.localizationService.transform('Reports.sentiment.neutral'),
                this.localizationService.transform('Reports.sentiment.negative'),
                this.localizationService.transform('Reports.sentiment.notAvailable'),
            ],
            isSocialStyles: true,
            hundredPercentStacked: true,
            showValueAxis: true,
            appBaseHref: this.appBaseHref,
            hoverEffect: true,
            sortValues: false,
            adjustedBarHeightPercent: 75,
            bulletLabelFontWeight: 'normal',
            tooltipText:
                '[bold font-size:0.75rem]{categoryY}: [font-size: 0.75rem]{name}\n[font-size:0.75rem]{valueX} ({valueX.totalPercent.formatNumber(\'#.0\')}%)',
            titleTooltipText: '',
            subtitleTooltipText: '',
            tooltipHTML: this.socialChartTooltipService.getTooltipHTML(
                'sentimentBySocialTopicComparisonChart',
                '{categoryY}',
                '{valueX} ({valueX.totalPercent.formatNumber(\'#.0\')}%)',
                '{name}',
                'fa-solid fa-square'
            ),
            customCategoryTemplate:
                `<div
                    style="
                            display: flex;
                            justify-content: center;
                            align-items: center;
                            margin-right: 20px;
                            width: 30px;
                            height: 30px;
                            font-size: 0.875rem;
                            font-weight: 600;
                            color: #ffffff;
                            border-radius: 25px;
                            background-color: #5e6473;"
                >
                        <span> {category} </span>
                </div>`,
            'POSITIVE': {
                countLabel: this.localizationService.transform('Reports.sentiment.positive.count'),
                percentLabel: this.localizationService.transform('Reports.sentiment.positive.percent')
            },
            'NEGATIVE': {
                countLabel: this.localizationService.transform('Reports.sentiment.negative.count'),
                percentLabel: this.localizationService.transform('Reports.sentiment.negative.percent')
            },
            'NEUTRAL': {
                countLabel: this.localizationService.transform('Reports.sentiment.neutral.count'),
                percentLabel: this.localizationService.transform('Reports.sentiment.neutral.percent')
            },
            'NOT_AVAILABLE': {
                countLabel: this.localizationService.transform('Reports.sentiment.notavailable.count'),
                percentLabel: this.localizationService.transform('Reports.sentiment.notavailable.percent')
            }
        };
    }


    private setSocialMediaSentimentTrendChartOptions(): void {
        const chartStyles = new ChartStylesModel();
        chartStyles.colorMap = {
            'POSITIVE': this.defaultChartColors.sentimentColors.positive,
            'NEUTRAL': this.defaultChartColors.sentimentColors.neutral,
            'NEGATIVE': this.defaultChartColors.sentimentColors.negative,
            'NOT_AVAILABLE': this.defaultChartColors.sentimentColors.notAvailable,
        };
        chartStyles.chartHeight = 400;
        chartStyles.titleFontSize = 18;
        chartStyles.headerMarginBottom = 30;
        this.socialMediaSentimentTrendChartOptions = {
            seriesDefinitions: [
                {
                    key: 'NEGATIVE',
                    label: this.localizationService.transform('Reports.sentiment.negative')
                },
                {
                    key: 'NOT_AVAILABLE',
                    label: this.localizationService.transform('Reports.sentiment.notAvailable')
                },
                {
                    key: 'NEUTRAL',
                    label: this.localizationService.transform('Reports.sentiment.neutral')
                },
                {
                    key: 'POSITIVE',
                    label: this.localizationService.transform('Reports.sentiment.positive')
                },
            ],
            styles: chartStyles,
            tooltipHTML: this.socialChartTooltipService.getMultiSeriesSharedTooltipHTML(
                'stackedRiverChartSocialNetwork',
                '{key} ({keyPercent.formatNumber(\'#.0\')}%)',
                '{legendLabel}',
                'fa-solid fa-square'
            )
        };
    }

    private setSocialTopicSentimentTrendChartOptions(): void {
        const chartStyles = new ChartStylesModel();
        chartStyles.colorMap = {
            'POSITIVE': this.defaultChartColors.sentimentColors.positive,
            'NEUTRAL': this.defaultChartColors.sentimentColors.neutral,
            'NEGATIVE': this.defaultChartColors.sentimentColors.negative,
            'NOT_AVAILABLE': this.defaultChartColors.sentimentColors.notAvailable,
        };
        chartStyles.chartHeight = 400;
        chartStyles.titleFontSize = 18;
        chartStyles.headerMarginBottom = 30;
        this.socialTopicSentimentTrendChartOptions = {
            seriesDefinitions: [
                {
                    key: 'NEGATIVE',
                    label: this.localizationService.transform('Reports.sentiment.negative')
                },
                {
                    key: 'NOT_AVAILABLE',
                    label: this.localizationService.transform('Reports.sentiment.notAvailable')
                },
                {
                    key: 'NEUTRAL',
                    label: this.localizationService.transform('Reports.sentiment.neutral')
                },
                {
                    key: 'POSITIVE',
                    label: this.localizationService.transform('Reports.sentiment.positive')
                },
            ],
            styles: chartStyles,
            tooltipHTML: this.socialChartTooltipService.getMultiSeriesSharedTooltipHTML(
                'stackedRiverChartSocialTopic',
                '{key} ({keyPercent.formatNumber(\'#.0\')}%)',
                '{legendLabel}',
                'fa-solid fa-square'
            ),
            isSocialStyles: true
        };
    }

    private setSummaryBySocialTopicChartOptions() {
        const summaryBySocialTopicChartStyles: ChartStylesModel = new ChartStylesModel({
            colorMap: {
                'seriesColor0': this.defaultChartColors.otherColors.volume,
                'seriesColor1': this.defaultChartColors.otherColors.impressions,
                'seriesColor2': this.defaultChartColors.otherColors.engagements,
            }
        });
        // Height used by the drilldown
        summaryBySocialTopicChartStyles.chartHeight = 422;
        this.summaryBySocialTopicChartOptions = {
            isMultiAxis: true,
            label: 'Label',
            startTimeLabel: 'Time Start',
            unit: DateAndValueXYChartUnitEnum.NUMBER,
            yValueLabel: 'Count',
            styles: summaryBySocialTopicChartStyles,
            tooltipHTML: this.socialChartTooltipService.getTooltipHTML(
                'summaryBySocialTopic',
                '{dateX}',
                '{valueY}',
                '{name}',
                'fa-solid fa-code-commit'
            ),
            isSocialStyles: true
        };
    }

    private setSummaryBySocialNetworkChartOptions() {
        const summaryBySocialNetworkChartStyles: ChartStylesModel = new ChartStylesModel({
            colorMap: {
                'seriesColor0': this.defaultChartColors.otherColors.volume,
                'seriesColor1': this.defaultChartColors.otherColors.impressions,
                'seriesColor2': this.defaultChartColors.otherColors.engagements,
            }
        });
        // Height used by the drilldown
        summaryBySocialNetworkChartStyles.chartHeight = 422;
        this.summaryBySocialNetworkChartOptions = {
            isMultiAxis: true,
            label: 'Label',
            startTimeLabel: 'Time Start',
            unit: DateAndValueXYChartUnitEnum.NUMBER,
            yValueLabel: 'Count',
            styles: summaryBySocialNetworkChartStyles,
            tooltipHTML: this.socialChartTooltipService.getTooltipHTML(
                'summaryBySocialNetwork',
                '{dateX}',
                '{valueY}',
                '{name}',
                'fa-solid fa-code-commit'
            ),
            isSocialStyles: true
        };
    }

    private setEngagementChartOptions(): void {
        const styles = new ChartStylesModel();
        styles.chartHeight = 290;
        styles.innerRadiusPercent = this.isArchiveSearch ? 35 : 15;
        styles.seriesRadiusPercent = [35, 60];
        styles.titleLabelColor = this.defaultChartColors.otherColors.textColor;
        styles.colorMap = {
            'APPLAUSE': this.defaultChartColors.otherColors.volume,
            'CONVERSATION': this.defaultChartColors.otherColors.impressions,
            'AMPLIFICATION': this.defaultChartColors.otherColors.engagements,
        };
        /* eslint-disable @typescript-eslint/naming-convention */
        styles.titleStyles = {
            'line-height': 1.5,
            'font-weight': '600'
        };
        this.engagementChartOptions = {
            styles: styles,
            customLegendItems: [
                {
                    label: this.localizationService.transform(`Social.intelligence.themeAnalytics.engagement.${EngagementTypeEnum.applause}`),
                    color: this.defaultChartColors.otherColors.volume
                },
                {
                    label: this.localizationService.transform(`Social.intelligence.themeAnalytics.engagement.${EngagementTypeEnum.conversation}`),
                    color: this.defaultChartColors.otherColors.impressions
                },
                {
                    label: this.localizationService.transform(`Social.intelligence.themeAnalytics.engagement.${EngagementTypeEnum.amplification}`),
                    color: this.defaultChartColors.otherColors.engagements
                }
            ],
            tooltipHTML: this.socialChartTooltipService.getTooltipHTML(
                'engagementSummaryChart',
                '{category}{labelInTooltip}',
                '{value.value} ({value.percent.formatNumber(\'#.0\')}%)',
                null,
                'fa-solid fa-square'
            ),
            isSocialStyles: true
        };
    }

    getChartOptions(chartType: ThemeAnalyticsChartTypeEnum): AbstractChartOptionsModel {
        return this.chartTypeToOptionsMap.get(chartType);
    }

    ngOnDestroy(): void {
        this.subscriptions.map(subscription => subscription.unsubscribe());
    }
}
